<script lang="ts">
    import { getContext, onMount } from 'svelte'
    import { writable, type Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'

    import { cardHeaderStyle } from 'hydria/context'
    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { escapeHtml } from 'hydria/utils/html'
    import { DateTime, toLuxon } from 'hydria/utils/time'

    import AddEmail from './AddEmail.svelte'
    import AddOtp from './AddOtp.svelte'
    import AddPasskey from './AddPasskey.svelte'
    import AddSms from './AddSms.svelte'
    // import HelpButton from 'hydria/components/HelpButton.svelte'
    import EditEmail from './EditEmail.svelte'
    import EditPasskey from './EditPasskey.svelte'
    import EditPassword from './EditPassword.svelte'
    import Log from './Log.svelte'
    import Sessions from './Sessions.svelte'
    import ViewRecoveryCodes from './ViewRecoveryCodes.svelte'

    interface Props {
        tokenExpired: () => void
    }

    let { tokenExpired }: Props = $props()

    let stoken = getContext('securityToken') as Writable<string>

    let editPassword: boolean = $state(false)
    let editEmail: boolean = $state(false)
    let editOtp: boolean = $state(false)
    let editRecoveryCodes: boolean = $state(false)
    let addSms: boolean = $state(false)
    let addEmail: boolean = $state(false)
    let accountEmail: string = $state('')

    let editPasskey: PkEntry | boolean = $state(false)

    interface Entry {
        id: string
        ctime: DateTime
        last_used: DateTime
        type: 'sms' | 'totp' | 'email'
        code: string
        comment: string
    }
    interface PkEntry {
        id: string
        ctime: DateTime
        last_used: DateTime
        name: string
        transports: string[]
    }

    let loading: boolean = $state(true)
    let otpMethods: Set<'recovery' | 'totp' | 'sms' | 'email'> = $state(new Set())
    let configurationAllowed = $state(false)
    let totp: undefined | Entry = $state(undefined)
    let emails: Entry[] = $state([])
    let phones: Entry[] = $state([])
    let showPhones: boolean = true
    let hasRecoveryCodes: boolean = $state(false)
    let signinNotifications: boolean = $state(false)

    let passkeys: PkEntry[] = $state([])

    type tVisibleSections = Set<'password' | 'user-email' | 'otp' | 'totp' | 'recovery' | 'sms' | 'email' | 'notifications' | 'log' | 'passkey' | 'sessions'>
    let visibleSections: tVisibleSections = $state(new Set())

    let preDelete: Entry | undefined = $state(undefined)
    let preDeletePkey: PkEntry | undefined = $state(undefined)

    let updInc = writable(0)

    async function refresh(simple: boolean = false) {
        if (!simple && !loading) load()
        updInc.update((v) => v + 1)
    }
    async function load() {
        loading = true

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', '/json/security/1/twofa', undefined, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            notify({
                type: 'danger',
                title: $_('Sécurité'),
                text: $_('Security data could not be loaded. Please try again.'),
            })
            console.error('Failed to load security data:', r)
            return
        }

        await importData(r.data)
        loading = false
    }

    async function importData(data: any) {
        const lemails: Entry[] = []
        const lphones: Entry[] = []
        totp = undefined

        let lVisibleSections = new Set(['password', 'user-email', 'log', 'sessions']) as tVisibleSections

        otpMethods = new Set(data.allowed_twofa_methods)
        configurationAllowed = data.security_configuration_allowed
        hasRecoveryCodes = data.has_recovery_codes
        signinNotifications = data.signin_notifications

        if (configurationAllowed) lVisibleSections.add('notifications')

        for (const m of otpMethods) {
            lVisibleSections.add(m)
        }

        if (hasRecoveryCodes) lVisibleSections.add('recovery')

        for (const item of data.items) {
            item.ctime = toLuxon(item.ctime)
            if (item.last_used) item.last_used = toLuxon(item.last_used)

            switch (item.type) {
                case 'totp':
                    totp = item
                    lVisibleSections.add('totp')
                    break
                case 'email':
                    lemails.push(item)
                    lVisibleSections.add('email')
                    break
                case 'sms':
                    lphones.push(item)
                    lVisibleSections.add('sms')
                    break
            }
        }

        emails = lemails
        phones = lphones

        if ([...lVisibleSections].filter((i) => ['totp', 'email', 'sms', 'recovery'].includes(i)).length > 0) {
            lVisibleSections.add('otp')
        }

        let lPasskeys: PkEntry[] = []
        if (data.passkey_possible) {
            lVisibleSections.add('passkey')
            if (data.passkey_items && data.passkey_items.length > 0) {
                for (const item of data.passkey_items) {
                    item.ctime = toLuxon(item.ctime)
                    if (item.last_used) item.last_used = toLuxon(item.last_used)
                }
                lPasskeys = data.passkey_items
            }
        }
        passkeys = lPasskeys

        visibleSections = lVisibleSections

        accountEmail = data.account_email
    }

    function pnum(show: boolean, phone: string) {
        if (show) {
            return phone
        }
        // For now, assume +1.aaabbbcccc
        return `+1.XXXXXX${phone.slice(-4)}`
    }

    let working = $state(false)
    async function removeEntry(entry: Entry | undefined) {
        if (entry == null || working) return

        let r: SimpleFetchResult

        working = true

        try {
            r = await simpleFetch('DELETE', `/json/security/1/twofa/${entry.id}`, {}, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            working = false
            return
        }
        notify({
            type: 'success',
            title: $_('Two-Factor Authentication'),
            text: $_('The entry has been removed.'),
        })
        working = false
        preDelete = undefined
        await importData(r.data)
        refresh(true)
    }
    async function removePasskey(entry: PkEntry | undefined) {
        if (entry == null || working) return

        let r: SimpleFetchResult

        working = true

        try {
            r = await simpleFetch('DELETE', `/json/security/1/passkey/${entry.id}`, {}, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            working = false
            return
        }
        notify({
            type: 'success',
            title: $_('Clé d’accès'),
            text: $_('La clé d’accès a été enlevée.'),
        })
        working = false
        preDeletePkey = undefined
        await importData(r.data)
        refresh(true)
    }

    async function setSigninNotifications(active: boolean) {
        if (working) return

        let r: SimpleFetchResult

        working = true

        try {
            r = await simpleFetch(active ? 'PUT' : 'DELETE', `/json/security/1/signin-notifications`, {}, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            working = false
            return
        }
        let m: string
        if (active) {
            m = $_('Vous recevrez maintenant un courriel à chaque connexion.')
        } else {
            m = $_('Vous ne recevrez plus de courriel lors d’une connexion.')
        }
        notify({
            type: 'success',
            title: $_('Notifications'),
            text: m,
        })
        working = false
        await importData(r.data)
        refresh(true)
    }

    onMount(() => {
        load()
    })
</script>

<style>
    .entry {
        display: grid;
        grid-template-columns: auto 1fr auto;
        grid-template-areas: 'icn name actions' '. desc .';
        gap: 0.25rem 0.5rem;
        padding: 0.25rem 0.5rem;
        align-items: center;
    }
    .entry .icn {
        grid-area: icn;
        font-size: 1.5em;
    }
    .entry .name {
        grid-area: name;
    }
    .entry .actions {
        grid-area: actions;
    }
    .entry .desc {
        grid-area: desc;
    }
    /* .entry.grp {
        grid-template-columns: 1fr;
        grid-template-areas: 'desc';
        padding: 0.25rem 0.5rem;
    } */
    .entries {
        border: 1px solid var(--bs-border-color);
        border-radius: var(--bs-border-radius-sm);
    }
    .entry:not(:last-child) {
        border-bottom: 1px solid var(--bs-border-color);
    }

    .pnum {
        font-feature-settings:
            'ss01' 1,
            'tnum' 1,
            'ss02' 1;
    }
    table.phone-list {
        max-width: 30em;
    }
    table.phone-list td {
        vertical-align: middle;
    }
    td .cmt {
        display: block;
        margin-top: 0.15em;
        font-size: 0.9em;
        color: #aaa;
    }
    .pact {
        text-align: right;
    }
</style>

{#if visibleSections.has('user-email')}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Email address')}</span>
        </div>
        <div class="card-body">
            <p>{@html $_('L’adresse courriel <code>{email}</code> est utilisée pour la connexion au compte, et pour la communication générale.', { email: escapeHtml(accountEmail) })}</p>
            {#if configurationAllowed}
                <p><button class="btn btn-outline-primary btn-sm" type="button" disabled={loading || working} onclick={() => (editEmail = true)}>{$_('Changer l’adresse courriel')}</button></p>
            {:else}
                <p class="text-muted">{$_('La modification est administrativement désactivée')}</p>
            {/if}
        </div>
    </div>
{/if}

{#if editEmail}
    <EditEmail
        currentEmail={accountEmail}
        close={() => {
            editEmail = false
        }}
        saved={() => {
            refresh()
        }}
    />
{/if}

{#if visibleSections.has('password')}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Password')}</span>
            <!-- <span class="actions"><HelpButton tag="security-password" /></span> -->
        </div>
        <div class="card-body">
            <p>{$_('Renforcez la sécurité de votre compte en choisissant un mot de passe solide.')}</p>
            {#if configurationAllowed}
                <p><button class="btn btn-outline-primary btn-sm" type="button" disabled={loading || working} onclick={() => (editPassword = true)}>{$_('Changer le mot de passe')}</button></p>
            {:else}
                <p class="text-muted">{$_('La modification est administrativement désactivée')}</p>
            {/if}

            {#if visibleSections.has('notifications')}
                {#if signinNotifications}
                    <p>{$_('Un courriel vous est envoyé à chaque connexion.')}</p>
                    <p>
                        <button class="btn btn-outline-danger btn-sm" type="button" disabled={loading || working} onclick={() => setSigninNotifications(false)}>
                            {$_('Désactiver ces notifications')}
                            {#if working}<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>{/if}
                        </button>
                    </p>
                {:else}
                    <p>{$_('Vous pouvez activer la fonctionnalité de recevoir un courriel chaque fois qu’une connexion est effectuée sur votre compte.')}</p>
                    <p>
                        <button class="btn btn-outline-secondary btn-sm" type="button" disabled={loading || working} onclick={() => setSigninNotifications(true)}>
                            {#if working}<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>{/if}
                            {$_('Activer la notification de connexion')}
                        </button>
                    </p>
                {/if}
            {/if}
        </div>
    </div>
{/if}

{#if editPassword}
    <EditPassword
        close={() => {
            editPassword = false
        }}
        saved={() => {
            refresh()
        }}
        {tokenExpired}
    />
{/if}

{#if visibleSections.has('passkey')}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Clés d’accès')}</span>
            <!-- <span class="actions"><HelpButton tag="security-passkey" /></span> -->
        </div>
        <div class="card-body">
            <p>{$_('Remplace l’utilisation du mot de passe par une validation à l’aide d’une clé d’accès.')}</p>
            {#if passkeys.length > 0}
                <table class="table phone-list">
                    <thead>
                        <tr>
                            <th class="pnum">
                                {$_('Nom')}
                            </th>
                            <th class="pact"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each passkeys as pkey}
                            <tr class="phone-entry">
                                <!-- <td class="pdesc">{phone.comment ? phone.comment : ''}</td> -->
                                <td class="pnum">
                                    <span
                                        >{#if pkey.name}{pkey.name}{:else}<em class="text-muted">{$_('Sans nom')}</em>{/if}</span
                                    >
                                </td>
                                <td class="pact">
                                    {#if configurationAllowed}
                                        <button type="button" class="btn btn-outline-secondary btn-sm" disabled={loading || working} onclick={() => (editPasskey = pkey)}><Icon inline icon="mdi:edit" /></button>
                                        <button
                                            type="button"
                                            class="btn btn-outline-secondary btn-sm"
                                            disabled={loading || working}
                                            onclick={() => {
                                                preDeletePkey = pkey
                                            }}><Icon inline icon="mdi:remove" /></button
                                        >
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            {:else}
                {$_('Aucune clé d’accès n’est en fonction.')}
            {/if}

            <p>
                <button
                    class="btn btn-outline-secondary"
                    type="button"
                    disabled={loading || working || !configurationAllowed}
                    onclick={() => {
                        editPasskey = true
                    }}>{$_('Ajouter une clé d’accès')}</button
                >
            </p>
        </div>
    </div>
{/if}
{#if editPasskey === true}
    <AddPasskey
        close={() => {
            editPasskey = false
        }}
        saved={() => {
            refresh()
        }}
        {tokenExpired}
    />
{:else if editPasskey}
    <EditPasskey
        currentName={editPasskey.name}
        keyId={editPasskey.id}
        close={() => {
            editPasskey = false
        }}
        saved={() => {
            refresh()
        }}
    />
{/if}

{#if visibleSections.has('otp')}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Authentification à deux facteurs')}</span>
            <!-- <span class="actions"><HelpButton tag="security-2fa" /></span> -->
        </div>
        <div class="card-body">
            <p>{$_('Ajoute une couche additionnelle de sécurité sur votre compte, en ajoutant une étape supplémentaire pour vous connecter, en plus d’entrer votre mot de passe.')}</p>
            {#if !configurationAllowed}
                <p class="text-muted">{$_('La modification est administrativement désactivée')}</p>
            {/if}

            {#if visibleSections.has('totp') || visibleSections.has('sms') || visibleSections.has('email')}
                <div class="entries" class:mb-3={visibleSections.has('recovery')}>
                    {#if visibleSections.has('totp')}
                        <div class="entry">
                            <div class="icn"><Icon inline icon="mdi:cellphone" /></div>
                            <div class="name">
                                {$_('Application d’authentification')}
                                {#if totp}
                                    <span class="badge bg-success text-light">{$_('Configuré')}</span>
                                {/if}
                            </div>
                            <div class="desc text-muted">
                                {$_('Utiliser une application ou une extension à votre navigateur pour obtenir un code de connexion lorsque demandé.')}
                            </div>
                            <div class="actions">
                                {#if totp && configurationAllowed}
                                    {#if otpMethods.has('totp')}
                                        <button type="button" class="btn btn-outline-secondary btn-sm" disabled={loading || working} onclick={() => (editOtp = true)}><Icon inline icon="mdi:edit" /></button>
                                    {/if}
                                    <button
                                        type="button"
                                        class="btn btn-outline-secondary btn-sm"
                                        disabled={!configurationAllowed || loading || working}
                                        onclick={() => {
                                            preDelete = totp
                                        }}><Icon inline icon="mdi:remove" /></button
                                    >
                                {:else if configurationAllowed && otpMethods.has('totp')}
                                    <button type="button" class="btn btn-outline-secondary btn-sm" disabled={!configurationAllowed || loading || working} onclick={() => (editOtp = true)}><Icon inline icon="mdi:add" /></button>
                                {/if}
                            </div>
                        </div>
                    {/if}<!-- totp -->

                    {#if visibleSections.has('sms')}
                        <div class="entry">
                            <div class="icn"><Icon inline icon="mdi:message-text" /></div>
                            <div class="name">
                                {$_('SMS / Text message')}
                                {#if phones.length > 0}
                                    <span class="badge bg-success text-light">{$_('Configuré')}</span>
                                {/if}
                            </div>
                            <div class="desc text-muted">
                                {#if phones.length > 0}
                                    <p>{$ngettext('Vous recevrez un code à utilisation unique au numéro suivant.', 'Vous recevrez un code à utilisation unique aux numéros suivants.', { n: phones.length })}</p>
                                    <table class="table phone-list">
                                        <thead>
                                            <tr>
                                                <th class="pnum">
                                                    {$_('Numéro')}
                                                </th>
                                                <th class="pact"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {#each phones as phone}
                                                <tr class="phone-entry">
                                                    <!-- <td class="pdesc">{phone.comment ? phone.comment : ''}</td> -->
                                                    <td class="pnum">
                                                        <span>{pnum(showPhones, phone.code)}</span>
                                                        {#if phone.comment}<span class="cmt">{phone.comment}</span>{/if}
                                                    </td>
                                                    <td class="pact">
                                                        {#if configurationAllowed}
                                                            <button
                                                                type="button"
                                                                class="btn btn-outline-secondary btn-sm"
                                                                disabled={loading || working}
                                                                onclick={() => {
                                                                    preDelete = phone
                                                                }}><Icon inline icon="mdi:trash" /></button
                                                            >
                                                        {/if}
                                                    </td>
                                                </tr>
                                            {/each}
                                        </tbody>
                                    </table>
                                {:else}
                                    {$_('Recevoir un code à utilisation unique à un numéro de cellulaire pour se connecter.')}
                                {/if}
                            </div>
                            <div class="actions">
                                {#if phones.length < 1 && configurationAllowed && otpMethods.has('sms')}
                                    <button type="button" class="btn btn-outline-secondary btn-sm" disabled={loading || working} onclick={() => (addSms = true)}><Icon inline icon="mdi:add" /></button>
                                {/if}
                            </div>
                        </div>
                    {/if}<!-- sms -->

                    {#if visibleSections.has('email')}
                        <div class="entry">
                            <div class="icn"><Icon inline icon="mdi:email" /></div>
                            <div class="name">
                                {$_('Email')}
                                {#if emails.length > 0}
                                    <span class="badge bg-success text-light">{$_('Configuré')}</span>
                                    <!-- <span class="text-success">
                                        <Icon inline icon="mdi:check" />
                                    </span> -->
                                {/if}
                            </div>
                            <div class="desc text-muted">
                                {#if emails.length > 0}
                                    <p>{$ngettext('Vous recevrez un courriel contenant un code à utilisation unique à l’adresse suivante.', 'Vous recevrez un courriel contenant un code à utilisation unique aux adresses suivantes.', { n: emails.length })}</p>
                                    <table class="table phone-list">
                                        <thead>
                                            <tr>
                                                <th class="pnum">
                                                    {$_('Adresse')}
                                                </th>
                                                <th class="pact"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {#each emails as email}
                                                <tr class="phone-entry">
                                                    <td class="pnum">
                                                        <span>{email.code}</span>
                                                        {#if email.comment}<span class="cmt">{email.comment}</span>{/if}
                                                    </td>
                                                    <td class="pact">
                                                        {#if configurationAllowed}
                                                            <button
                                                                type="button"
                                                                class="btn btn-outline-secondary btn-sm"
                                                                disabled={loading || working}
                                                                onclick={() => {
                                                                    preDelete = email
                                                                }}><Icon inline icon="mdi:trash" /></button
                                                            >
                                                        {/if}
                                                    </td>
                                                </tr>
                                            {/each}
                                        </tbody>
                                    </table>
                                {:else}
                                    {$_('Recevoir un courriel contenant un code à utilisation unique pour se connecter.')}
                                {/if}
                            </div>
                            <div class="actions">
                                {#if emails.length < 1 && configurationAllowed && otpMethods.has('email')}
                                    <button type="button" class="btn btn-outline-secondary btn-sm" disabled={loading || working} onclick={() => (addEmail = true)}><Icon inline icon="mdi:add" /></button>
                                {/if}
                            </div>
                        </div>
                    {/if}<!-- email -->
                </div>
            {/if}<!-- totp|sms|email -->

            {#if visibleSections.has('recovery')}
                <div class="entries">
                    <div class="entry">
                        <div class="icn"><Icon inline icon="mdi:key" /></div>
                        <div class="name">
                            {$_('Codes de récupération')}
                            {#if hasRecoveryCodes}
                                <span class="badge bg-success text-light">{$_('Configuré')}</span>
                            {/if}
                        </div>
                        <div class="desc text-muted">
                            {$_('Les codes de récupération permettent d’accéder à votre compte, et la section de sécurité, au cas où il devient impossible de se connecter à l’aide de vos méthodes d’authentification à deux facteurs.')}
                        </div>
                        <div class="actions">
                            {#if hasRecoveryCodes}
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    disabled={loading || working}
                                    onclick={() => {
                                        editRecoveryCodes = true
                                    }}><Icon inline icon="mdi:eye" /></button
                                >
                            {:else}
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    disabled={loading || working}
                                    onclick={() => {
                                        editRecoveryCodes = true
                                    }}><Icon inline icon="mdi:add" /></button
                                >
                            {/if}
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
{/if}<!--otp-->

{#if editOtp}
    <AddOtp
        hadCode={totp != null}
        close={() => {
            editOtp = false
        }}
        saved={() => {
            refresh()
        }}
        {tokenExpired}
    />
{/if}
{#if addSms}
    <AddSms
        close={() => {
            addSms = false
        }}
        saved={() => {
            refresh()
        }}
        {tokenExpired}
    />
{/if}
{#if addEmail}
    <AddEmail
        close={() => {
            addEmail = false
        }}
        saved={() => {
            refresh()
        }}
        {tokenExpired}
    />
{/if}
{#if editRecoveryCodes}
    <ViewRecoveryCodes
        readOnly={!configurationAllowed || !otpMethods.has('recovery')}
        close={() => {
            editRecoveryCodes = false
        }}
        saved={() => {
            refresh()
        }}
        {tokenExpired}
    />
{/if}

{#if preDelete != null || preDeletePkey != null}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{$_('Désactiver')}</div></div>
                <div class="modal-body">
                    {#if preDeletePkey != null}
                        <p>{$_('Vous êtes sur le point de désactiver une clé d’accès.')}</p>
                    {:else if preDelete && preDelete.type == 'sms'}
                        {#if preDelete.comment}
                            <p>{$_('Vous êtes sur le point d’enlever le téléphone {num} (intitulé « {comment} ») comme option d’authentification à deux facteurs.', { num: pnum(false, preDelete.code), comment: preDelete.comment })}</p>
                        {:else}
                            <p>{$_('Vous êtes sur le point d’enlever le téléphone {num} comme option d’authentification à deux facteurs.', { num: pnum(false, preDelete.code) })}</p>
                        {/if}
                    {:else if preDelete && preDelete.type == 'email'}
                        <p>{$_('Vous êtes sur le point de désactiver l’utilisation d’un courriel comme option d’authentification à deux facteurs.')}</p>
                    {:else if preDelete && preDelete.type == 'totp'}
                        <p>{$_('Vous êtes sur le point de désactiver l’utilisation d’une application d’authentification.')}</p>
                    {/if}
                </div>
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        disabled={working}
                        onclick={() => {
                            preDelete = undefined
                            preDeletePkey = undefined
                        }}>{$_('Cancel')}</button
                    >
                    <button
                        type="button"
                        class="btn btn-danger"
                        disabled={working}
                        onclick={() => {
                            if (preDeletePkey != null) removePasskey(preDeletePkey)
                            else if (preDelete) removeEntry(preDelete)
                        }}>{$_('Remove')}</button
                    >
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                if (!working) {
                    preDelete = undefined
                    preDeletePkey = undefined
                }
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}

{#if visibleSections.has('sessions')}
    <Sessions {updInc} />
{/if}

{#if visibleSections.has('log')}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Journal')}</span>
        </div>
        <div class="card-body">
            <Log {updInc} />
        </div>
    </div>
{/if}
